import { useEffect, useState } from 'react';
import { Spinner } from '@monksflow/monks-ui';
import { AlertTriangleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  downloadUrl: string;
  fileName: string;
}

/**
 * 1 MB cap. Above that we refuse to inline the text — browsers tank on huge
 * <pre> nodes and the user can still hit Download for the full file.
 */
const MAX_INLINE_BYTES = 1_000_000;

type State =
  | { status: 'loading' }
  | { status: 'ready'; text: string; truncated: boolean }
  | { status: 'error'; message: string };

/**
 * Module-level LRU cache of fetched text bodies, keyed by the asset's stable
 * URL path (signed-URL query params stripped — the BE re-signs on every
 * /detail call, so the full URL changes between mounts even for the same
 * asset). Lets re-opens (Library preview navigation back to a previously-
 * viewed .txt) render the content synchronously on mount.
 *
 * Capped at 16 entries to bound memory across long Library sessions; LRU
 * eviction via insertion-order re-insert on read keeps the hottest artifacts
 * resident.
 */
const TEXT_CACHE_MAX = 16;
const textCache = new Map<string, State & { status: 'ready' }>();

function readTextCache(key: string): (State & { status: 'ready' }) | undefined {
  const hit = textCache.get(key);
  if (!hit) return undefined;
  // Refresh recency: re-insert as the most-recent entry.
  textCache.delete(key);
  textCache.set(key, hit);
  return hit;
}

function writeTextCache(key: string, value: State & { status: 'ready' }): void {
  if (textCache.has(key)) textCache.delete(key);
  textCache.set(key, value);
  while (textCache.size > TEXT_CACHE_MAX) {
    const oldest = textCache.keys().next().value;
    if (oldest === undefined) break;
    textCache.delete(oldest);
  }
}

function cacheKeyFor(downloadUrl: string): string {
  const queryIndex = downloadUrl.indexOf('?');
  return queryIndex === -1 ? downloadUrl : downloadUrl.slice(0, queryIndex);
}

export function TextViewer(props: Props) {
  const { t } = useTranslation('projects');

  const [state, setState] = useState<State>(
    () => readTextCache(cacheKeyFor(props.downloadUrl)) ?? { status: 'loading' },
  );

  useEffect(() => {
    const key = cacheKeyFor(props.downloadUrl);
    // Cache hit — sync state to the cached entry (the initializer only fires
    // on the first mount; later `downloadUrl` changes within the same
    // component instance need an explicit `setState` to switch bodies).
    const cached = readTextCache(key);
    if (cached) {
      setState(cached);
      return;
    }
    // AbortController so unmount / `downloadUrl` change cancels the in-flight
    // request AND the reader loop, freeing the buffer instead of letting it
    // accumulate up to MAX_INLINE_BYTES in the background.
    const ac = new AbortController();
    setState({ status: 'loading' });

    fetch(props.downloadUrl, { signal: ac.signal })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        // Always stream up to MAX_INLINE_BYTES regardless of `content-length`.
        // The header is advisory: it can be absent, inaccurate, or missing on
        // chunked-transfer responses, and falling through to `res.text()`
        // would pull an arbitrarily large body into memory.
        const reader = res.body?.getReader();
        if (!reader) {
          throw new Error('No body reader available');
        }
        const decoder = new TextDecoder();
        let received = 0;
        let buffer = '';
        let truncated = false;
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          received += value.length;
          buffer += decoder.decode(value, { stream: true });
          if (received > MAX_INLINE_BYTES) {
            truncated = true;
            await reader.cancel();
            break;
          }
        }
        buffer += decoder.decode();
        return { text: buffer, truncated };
      })
      .then(({ text, truncated }) => {
        if (ac.signal.aborted) return;
        // eslint-disable-next-line i18next/no-literal-string -- viewer state discriminator
        const next = { status: 'ready' as const, text, truncated };
        writeTextCache(key, next);
        setState(next);
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        // eslint-disable-next-line i18next/no-literal-string -- diagnostic only regex-tested to pick which translated state to render
        const message = err instanceof Error ? err.message : 'Unknown error';
        setState({ status: 'error', message });
      });

    return () => {
      ac.abort();
    };
  }, [props.downloadUrl]);

  if (state.status === 'loading') {
    return (
      <div
        aria-busy="true"
        aria-live="polite"
        className="flex h-full items-center justify-center p-8"
      >
        <Spinner />
      </div>
    );
  }

  if (state.status === 'error') {
    // A 404/403 means the underlying asset is gone or unreachable (e.g. an
    // agent-generated export whose S3 object was cleaned up). Degrade to a
    // calm "unavailable" message instead of surfacing the raw `HTTP 404`.
    const isMissing = /\b40[34]\b/.test(state.message);
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-xl bg-muted dark:bg-background p-6 text-center">
        <AlertTriangleIcon className="size-8 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">
          {isMissing
            ? t('projectDetail.textViewer.thisFileIsNoLonger')
            : t('projectDetail.textViewer.couldnTPreviewThisFile')}
        </span>
        <span className="max-w-xs text-xs text-muted-foreground">
          {isMissing
            ? t('projectDetail.textViewer.theFileMayHaveBeen')
            : t('projectDetail.textViewer.somethingWentWrongLoadingThe')}
        </span>
        {!isMissing && (
          <a
            href={props.downloadUrl}
            download={props.fileName}
            className="mt-1 text-xs font-medium text-primary hover:underline"
          >
            {t('projectDetail.textViewer.downloadFile')}
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-xl bg-muted dark:bg-background">
      <pre className="flex-1 overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-sm leading-relaxed text-foreground">
        {state.text}
      </pre>
      {state.truncated && (
        <div className="border-t border-border bg-amber-50 px-4 py-2 text-xs text-amber-900">
          {t('projectDetail.textViewer.previewTruncatedAt', {
            sizeKb: Math.round(MAX_INLINE_BYTES / 1000),
          })}
        </div>
      )}
    </div>
  );
}
